import React from "react";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { Music2, Guitar, Handshake, Info, Instagram, Facebook, Globe, Mail, ExternalLink, Star, Music } from "lucide-react";

export default function MusiciensLive() {
  useSeo({
    title: "Musiciens Live — Saxophoniste · Violoniste · Guitariste | My Animation",
    description:
      "Forfaits musiciens live pour mariage, cocktail, gala, entreprise, mairie, guinguette, festival : saxophoniste, violoniste, guitariste, chanteur-guitariste, packs Duo, Trio et Groupe Live. Prestations réalisées par nos prestataires sous-traitants selon disponibilité.",
    path: "/musiciens-live",
    keywords:
      "saxophoniste mariage Nord, violoniste cérémonie Hauts-de-France, guitariste vin d'honneur, chanteur guitariste événement, duo live cocktail entreprise, trio live gala, groupe live festival, musiciens live Nord Pas-de-Calais Belgique",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Musiciens Live événementiel",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description:
      "Forfaits saxophoniste, violoniste, guitariste et packs live (Duo, Trio, Groupe) pour mariage, cocktail, gala, entreprise, mairie, guinguette, festival. Prestations réalisées via des prestataires sous-traitants sélectionnés par My Animation.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Musiciens Live"
        title={<>Un moment live <span className="italic gold-text">inoubliable</span><br />pour votre événement.</>}
        subtitle="Saxophoniste, violoniste, guitariste, chanteur-guitariste ou packs Duo / Trio / Groupe live — la musique en direct qui transforme un cocktail, une cérémonie ou une soirée en instant magique. Prestations réalisées par nos prestataires sous-traitants selon disponibilité."
        img="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?auto=format&fit=crop&w=2400&q=70"
      />

      {/* Bloc sous-traitance — mention légale importante */}
      <section className="py-14 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div
          className="card-luxe p-6 md:p-8 border-[#DCB457]/40 flex flex-col md:flex-row gap-6 items-start reveal"
          data-testid="musiciens-partners-note"
        >
          <Handshake size={28} strokeWidth={1.3} className="text-[#DCB457] shrink-0 mt-1" />
          <div>
            <p className="eyebrow">Nos musiciens partenaires</p>
            <h2 className="font-serif-luxe text-2xl md:text-3xl mt-2">
              Prestations réalisées par des <span className="italic gold-text">prestataires sous-traitants selon disponibilité.</span>
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed mt-4 max-w-3xl">
              Pour vous offrir un rendu réellement premium, les prestations musiciens live sont assurées via
              un ou plusieurs artistes partenaires professionnels, sélectionnés et coordonnés par My Animation.
              Vous conservez un seul interlocuteur, un seul devis, une seule facturation. Chaque musicien intervient
              avec son propre matériel professionnel et ses assurances, à nos côtés dans le Nord, le Pas-de-Calais,
              les Hauts-de-France et la Belgique.
            </p>
          </div>
        </div>
      </section>

      {/* Zoom saxophonistes partenaires — Eddsax & Brun'oSax */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal text-center">
            <span className="eyebrow">Nos saxophonistes partenaires</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Deux artistes <span className="italic gold-text">d&apos;exception.</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base leading-relaxed mt-5 max-w-3xl mx-auto">
              My Animation collabore régulièrement avec deux saxophonistes reconnus dans le Nord et les Hauts-de-France —
              chacun avec sa signature musicale, son répertoire et son univers scénique. Découvrez-les.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* --- EDDSAX --- */}
            <article className="card-luxe p-0 overflow-hidden reveal group" data-testid="artist-card-eddsax">
              <div className="relative h-72 md:h-80 overflow-hidden">
                <img
                  src="/assets/musiciens/eddsax-portrait.jpg"
                  alt="Eddsax — saxophoniste événementiel Nord Pas-de-Calais (Édouard Musiala)"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between gap-4">
                  <div>
                    <p className="eyebrow text-[#DCB457]/90">Saxophoniste · DJ Sax Live</p>
                    <h3 className="font-serif-luxe text-3xl md:text-4xl mt-1 text-white">Eddsax</h3>
                  </div>
                  <span className="flex items-center gap-1.5 text-[#DCB457] text-xs uppercase tracking-[0.18em]">
                    <Star size={14} strokeWidth={1.5} className="fill-[#DCB457]" /> 5,0 Google
                  </span>
                </div>
              </div>

              <div className="p-7 md:p-8">
                <p className="text-white/75 text-sm md:text-[15px] leading-relaxed">
                  <b className="text-white">Édouard Musiala</b> — saxophoniste événementiel spécialiste des soirées DJ,
                  mariages, cocktails lounge et soirées d&apos;entreprise. Sa signature&nbsp;: un mélange subtil de saxophone
                  live et de musique électronique <span className="italic">(Lounge, Jazzy, Pop, Electro Chill / Deep House)</span>.
                  Propose également des concepts <b className="text-white">Light Sax</b> et <b className="text-white">Pyro Sax Show</b>.
                </p>
                <p className="text-white/55 text-[13px] mt-4 leading-relaxed">
                  Interventions&nbsp;: Lille · Béthune · Lens · Arras · Cambrai · Le Touquet · Amiens · Paris · Belgique.
                </p>

                {/* Réseaux */}
                <div className="mt-6 pt-5 border-t border-[#DCB457]/15 flex flex-wrap gap-2.5">
                  <a
                    href="https://www.eddsax.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eddsax-website"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Globe size={13} strokeWidth={1.5} /> eddsax.com
                  </a>
                  <a
                    href="https://www.instagram.com/eddsax_official/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eddsax-instagram"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Instagram size={13} strokeWidth={1.5} /> @eddsax_official
                  </a>
                  <a
                    href="https://www.facebook.com/eddsaxonmusic/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eddsax-facebook"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Facebook size={13} strokeWidth={1.5} /> Facebook
                  </a>
                  <a
                    href="https://www.eddsax.com/musique"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eddsax-musique"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Music size={13} strokeWidth={1.5} /> Écouter
                  </a>
                </div>
              </div>
            </article>

            {/* --- BRUN'OSAX --- */}
            <article className="card-luxe p-0 overflow-hidden reveal group" data-testid="artist-card-brunosax">
              <div
                className="relative h-72 md:h-80 overflow-hidden flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(circle at 30% 40%, rgba(220,180,87,0.28) 0%, rgba(220,180,87,0.08) 45%, rgba(0,0,0,0.95) 100%), linear-gradient(135deg, #1a1208 0%, #0a0704 60%, #000 100%)",
                }}
              >
                {/* Ornement doré */}
                <div className="absolute inset-0 opacity-20" style={{
                  backgroundImage:
                    "radial-gradient(circle at 20% 30%, rgba(220,180,87,0.35) 0px, transparent 2px), radial-gradient(circle at 80% 70%, rgba(220,180,87,0.25) 0px, transparent 2px), radial-gradient(circle at 50% 50%, rgba(220,180,87,0.15) 0px, transparent 3px)",
                  backgroundSize: "60px 60px, 80px 80px, 100px 100px",
                }} />
                <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6">
                  <Music2 size={68} strokeWidth={0.8} className="text-[#DCB457]" />
                  <p className="text-white/50 text-[11px] uppercase tracking-[0.28em]">
                    Voir photos & vidéos sur Mariages.net
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between gap-4">
                  <div>
                    <p className="eyebrow text-[#DCB457]/90">Saxophoniste · Club Med Live</p>
                    <h3 className="font-serif-luxe text-3xl md:text-4xl mt-1 text-white">Brun&apos;oSax</h3>
                  </div>
                  <span className="flex items-center gap-1.5 text-[#DCB457] text-xs uppercase tracking-[0.18em]">
                    <Star size={14} strokeWidth={1.5} className="fill-[#DCB457]" /> 5,0 · 16 avis
                  </span>
                </div>
              </div>

              <div className="p-7 md:p-8">
                <p className="text-white/75 text-sm md:text-[15px] leading-relaxed">
                  <b className="text-white">Bruno Masclet</b> — saxophoniste événementiel professionnel, issu du conservatoire
                  (classique &amp; jazz), lecteur et improvisateur. Saxophoniste <b className="text-white">Club Med Live</b>
                  {" "}(19 programmations). Répertoire large&nbsp;:
                  <span className="italic"> jazz, lounge/électro, chill/deep, house, bossa nova, pop, rock</span>.
                  Formations disponibles&nbsp;: solo, duo Sax/DJ, trio, quatuor, quintet…
                </p>
                <p className="text-white/55 text-[13px] mt-4 leading-relaxed">
                  Basé à Valenciennes (59) · déplacements national sous conditions ·
                  <b className="text-white/70"> 100 % recommandé</b> par les couples sur Mariages.net.
                </p>

                {/* Réseaux */}
                <div className="mt-6 pt-5 border-t border-[#DCB457]/15 flex flex-wrap gap-2.5">
                  <a
                    href="https://www.mariages.net/musique-mariage/brunosax-saxophoniste--e272961"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="brunosax-mariages-net"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <ExternalLink size={13} strokeWidth={1.5} /> Mariages.net
                  </a>
                  <a
                    href="https://www.instagram.com/mascletbruno_brunosax/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="brunosax-instagram"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Instagram size={13} strokeWidth={1.5} /> @mascletbruno_brunosax
                  </a>
                  <a
                    href="https://www.facebook.com/BrunoSaxMusicLive/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="brunosax-facebook-page"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Facebook size={13} strokeWidth={1.5} /> BrunoSaxMusicLive
                  </a>
                  <a
                    href="https://www.facebook.com/bruno.masclet.5/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="brunosax-facebook-perso"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-[11px] uppercase tracking-[0.16em] text-white/80 hover:text-[#DCB457] border border-[#DCB457]/30 hover:border-[#DCB457]/70 transition rounded-sm"
                  >
                    <Facebook size={13} strokeWidth={1.5} /> Bruno Masclet
                  </a>
                </div>
              </div>
            </article>
          </div>

          <p className="text-center text-white/45 text-xs mt-10 uppercase tracking-[0.18em]">
            Autres musiciens (violonistes · guitaristes · chanteurs-guitaristes) mobilisés sur demande selon disponibilité.
          </p>
        </div>
      </section>

      {/* Mini lecteurs — extraits vidéo/audio */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="mb-12 reveal text-center">
          <span className="eyebrow">Écoutez-les</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Un aperçu <span className="italic gold-text">live.</span>
          </h2>
          <p className="text-white/60 text-sm md:text-base leading-relaxed mt-5 max-w-3xl mx-auto">
            Découvrez chaque saxophoniste en situation — quelques secondes suffisent pour ressentir leur style et
            leur énergie. Retrouvez d&apos;autres extraits sur leurs pages Instagram, YouTube ou site officiel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Eddsax — Instagram Reel embed */}
          <div className="card-luxe p-6 md:p-7 reveal" data-testid="artist-player-eddsax">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="eyebrow">Eddsax</p>
                <h3 className="font-serif-luxe text-2xl mt-1">Sax Live · Electro Chill</h3>
              </div>
              <a
                href="https://www.instagram.com/eddsax_official/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] uppercase tracking-[0.16em] text-[#DCB457] hover:opacity-80"
              >
                @eddsax_official ↗
              </a>
            </div>
            <div className="aspect-[9/16] max-h-[560px] mx-auto bg-black/50 border border-[#DCB457]/20 overflow-hidden">
              <iframe
                src="https://www.instagram.com/eddsax_official/embed/"
                title="Eddsax — Extraits Instagram"
                className="w-full h-full"
                frameBorder="0"
                scrolling="no"
                allow="encrypted-media"
                data-testid="eddsax-instagram-embed"
              />
            </div>
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              Le player affiche les publications récentes du compte Instagram d&apos;Eddsax. Pour plus d&apos;extraits, visitez sa page{" "}
              <a href="https://www.eddsax.com/musique" target="_blank" rel="noopener noreferrer" className="text-[#DCB457] hover:underline">musique dédiée</a>.
            </p>
          </div>

          {/* Brun'oSax — Instagram Reel embed */}
          <div className="card-luxe p-6 md:p-7 reveal" data-testid="artist-player-brunosax">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="eyebrow">Brun&apos;oSax</p>
                <h3 className="font-serif-luxe text-2xl mt-1">Jazz · Lounge · Deep House</h3>
              </div>
              <a
                href="https://www.instagram.com/mascletbruno_brunosax/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] uppercase tracking-[0.16em] text-[#DCB457] hover:opacity-80"
              >
                @mascletbruno_brunosax ↗
              </a>
            </div>
            <div className="aspect-[9/16] max-h-[560px] mx-auto bg-black/50 border border-[#DCB457]/20 overflow-hidden">
              <iframe
                src="https://www.instagram.com/mascletbruno_brunosax/embed/"
                title="Brun'oSax — Extraits Instagram"
                className="w-full h-full"
                frameBorder="0"
                scrolling="no"
                allow="encrypted-media"
                data-testid="brunosax-instagram-embed"
              />
            </div>
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              Le player affiche les publications récentes du compte Instagram de Brun&apos;oSax. Retrouvez également ses vidéos live
              sur sa page{" "}
              <a href="https://www.facebook.com/BrunoSaxMusicLive/" target="_blank" rel="noopener noreferrer" className="text-[#DCB457] hover:underline">Facebook BrunoSaxMusicLive</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Forfaits catalogue */}
      <CatalogPackages
        category="musiciens"
        title={<>Nos forfaits <span className="italic gold-text">musiciens live.</span></>}
        subtitle={<>Saxophoniste · Violoniste · Guitariste — packs Duo, Trio & <span className="italic gold-text">Groupe live.</span></>}
        testidPrefix="musiciens-pack-"
        footerNote="Tarifs TTC · TVA non applicable art. 293 B du CGI · Prestations réalisées par des prestataires sous-traitants selon disponibilité · Devis personnalisé selon date, lieu, durée, musicien, répertoire et besoins techniques"
      />

      {/* Trois cards catégorielles pour l'harmonie visuelle */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-12 reveal">
            <span className="eyebrow">Comment choisir</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">
              Solo, duo <span className="italic gold-text">ou groupe live&nbsp;?</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Music2,
                t: "Musicien solo",
                d: "Saxophoniste, violoniste ou guitariste seul — l'élégance pour une cérémonie, un vin d'honneur ou un cocktail. Trois niveaux : Essentiel (45 min), Événement (1h30) et Prestige (2h30).",
              },
              {
                icon: Guitar,
                t: "Pack Duo Live",
                d: "Deux artistes complémentaires — sax + chant, guitare + chant, violon + guitare. La formule qui monte en puissance pour un mariage, un cocktail entreprise ou une réception élégante.",
              },
              {
                icon: Music2,
                t: "Trio & Groupe Live",
                d: "Trois à cinq musiciens sur scène — véritable présence live pour un gala, un festival, une fête communale ou une soirée d'entreprise premium. Le grand format qui marque les esprits.",
              },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="card-luxe p-8 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-4" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/60 mt-3 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Événements adaptés */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="mb-10 reveal text-center">
          <span className="eyebrow">Pour tous vos événements</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">
            Une musique live <span className="italic gold-text">sur-mesure.</span>
          </h2>
          <p className="text-white/60 text-sm md:text-base leading-relaxed mt-5 max-w-3xl mx-auto">
            Mariage, anniversaire, baptême, communion, vin d&apos;honneur, cocktail, mairie, collectivité, guinguette,
            fête de la musique, festival, entreprise, séminaire, gala, inauguration ou soirée privée — nos musiciens
            s&apos;adaptent à votre ambiance, votre répertoire et votre déroulé.
          </p>
        </div>
      </section>

      {/* Mentions importantes */}
      <section className="py-14 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto">
        <div className="card-luxe p-6 md:p-8 flex items-start gap-4" data-testid="musiciens-legal">
          <Info size={18} className="text-[#DCB457] shrink-0 mt-1" />
          <div className="text-sm text-white/65 leading-relaxed">
            <p className="mb-3">
              <b className="text-white">Informations importantes.</b> Les tarifs sont indiqués à titre indicatif et peuvent
              varier selon : la date de l&apos;événement, le lieu, la durée demandée, le type de musicien, le nombre d&apos;artistes,
              le répertoire, le temps de préparation, les besoins en sonorisation, les contraintes d&apos;installation, les
              frais de déplacement et les horaires de prestation.
            </p>
            <p>
              Les prestations musiciens sont proposées sous réserve de disponibilité des artistes et prestataires
              partenaires. <b className="text-white">Prestation réalisée par un prestataire sous-traitant selon disponibilité.</b>
              {" "}Tarifs TTC · TVA non applicable, article 293 B du CGI.
            </p>
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}
